import { supabase } from '@/lib/supabase';

// ============================================================
// Prompts IA éditables et sauvegardables (par le superadmin).
//
// Chaque prompt est identifié par une clé (prompt_key). Les
// prompts sont stockés dans la table zifek_prompts pour pouvoir
// être modifiés depuis le Theme Builder sans redéploiement.
//
// Le prompt de conversion utilise des PLACEHOLDERS qui sont
// remplacés automatiquement au moment de l'appel à l'IA :
//   {{CATEGORIE}}       → titre de la catégorie de thème
//   {{TYPE_BLOCK}}      → bloc « type de thème » (préfixé de 2 sauts de ligne)
//   {{BUSINESS_BLOCK}}  → bloc « catégorie d'activité » (préfixé de 2 sauts de ligne)
//   {{REFERENCE_BLOCK}} → instructions du fichier de référence (ou message par défaut)
//   {{PAGES_LIST}}      → liste « - clé : libellé »
//   {{PAGES_KEYS}}      → liste des clés séparées par des virgules
// ============================================================

export const CONVERSION_PROMPT_KEY = 'theme_conversion';

export interface StoredPrompt {
  id: number;
  prompt_key: string;
  titre: string;
  contenu: string;
  updated_at: string | null;
}

/** Prompt de conversion par défaut (modèle livré avec la plateforme). */
export const DEFAULT_CONVERSION_PROMPT = `Tu es un expert en conversion de thèmes web vers le moteur de thèmes Zifek.

🎯 MISSION — LA PLUS IMPORTANTE
On te fournit une conversion automatique d'un thème existant (pages HTML + CSS).
Ta mission est de CONSERVER EXACTEMENT le design d'origine, et de rendre le thème DYNAMIQUE et conforme à Zifek.

🚫 NE CHANGE PAS LE DESIGN
- Ne change PAS la structure HTML existante (mêmes balises, mêmes sections, même ordre).
- Ne change PAS la position des éléments.
- Ne change PAS les classes CSS, les couleurs, les polices, les espacements, les tailles.
- Ne repositionne, n'ajoute et ne supprime AUCUN bloc, aucune carte, aucun bouton.
- Tu dois pouvoir Superposer « avant » et « après » : ils doivent être visuellement identiques, à l'exception des zones dynamiques.

✅ CE QUE TU DOIS FAIRE
- Rendre dynamiques UNIQUEMENT les zones de contenu : les textes de page, la bannière, les listes de produits/services/etc., en remplaçant le contenu figé par les balises Zifek correspondantes.
- Si une donnée n'a pas de balise Zifek correspondante, conserve le contenu statique tel quel (on ne casse rien).
- Nettoyer les résidus de l'ancien moteur (variables PHP, appels, attributs techniques) sans toucher au rendu visuel.

Catégorie : « {{CATEGORIE}} »{{TYPE_BLOCK}}{{BUSINESS_BLOCK}}

{{REFERENCE_BLOCK}}

⚠️ RÈGLES ABSOLUES
1. N'utilise QUE les template tags Zifek existants :
   - Simples : {{site_name}} {{site_description}} {{site_logo}} {{site_phone}} {{site_email}} {{site_address}} {{site_about}} {{site_url}} {{year}}
   - Blocs : {{products}} {{products limit="6"}} {{services}} {{blog_posts}} {{partners}} {{team}} {{portfolio}} {{testimonials}} {{stats}} {{contact_form}} {{booking_form}} {{faq}} {{gallery}} {{hebergements}} {{hero_banners}}
   - Marketplace : {{marketplace_products}} {{marketplace_sellers}} {{marketplace_search}} {{marketplace_categories}} {{featured_products}}
2. ⛔ INTERDIT : toute syntaxe d'un autre moteur de thèmes (style Liquid/Shopify) : {{merchant.name}}, {{merchant.email}}, {{cart.item_count}}, {{customer.name}}, {{product.name}}, {{product.price}}, {{category.name}}, {{post.title}}, {{routes.products}}, {{homepage.hero.title}} et les boucles {{#bloc}}…{{/bloc}}, {{else}}, {% … %}. N'invente AUCUNE balise pointée ou préfixée.
3. ⛔ INTERDIT : les boucles/sections manuelles. N'écris PAS {{#products}} … {{/products}} avec une répétition {{product.name}} à l'intérieur. Écris la balise unique {{products}} : le moteur génère toute la grille.
4. JAMAIS de JavaScript, de fetch, ni d'appels API dans le HTML.
5. Le CSS fourni doit être conservé (design identique). Tu peux ajouter les quelques règles de confort nécessaires au rendu dynamique, mais sans altérer l'apparence existante.
6. Les pages obligatoires doivent toutes exister : {{PAGES_KEYS}}.

📤 FORMAT DE RÉPONSE EXIGÉ (JSON strict, sans markdown) :
{
  "stylesheet": "CSS (identique au design d'origine, éventuellement complété)",
  "pages": { "home": "<section>...</section>" }
}
Renvoie toutes les clés de pages fournies. Réponds UNIQUEMENT avec le JSON.`;

/** Charge un prompt personnalisé. Retourne null s'il n'existe pas. */
export async function fetchPrompt(key: string): Promise<StoredPrompt | null> {
  try {
    const { data } = await supabase
      .from('zifek_prompts')
      .select('*')
      .eq('prompt_key', key)
      .maybeSingle();
    return (data as StoredPrompt) || null;
  } catch {
    return null;
  }
}

/** Charge le contenu d'un prompt (ou null). Best-effort. */
export async function fetchPromptContent(key: string): Promise<string | null> {
  const row = await fetchPrompt(key);
  const content = row?.contenu?.trim();
  return content ? content : null;
}

/** Crée ou met à jour un prompt. */
export async function savePrompt(key: string, titre: string, contenu: string): Promise<void> {
  const { data: existing } = await supabase
    .from('zifek_prompts')
    .select('id')
    .eq('prompt_key', key)
    .maybeSingle();
  if (existing?.id) {
    await supabase
      .from('zifek_prompts')
      .update({ titre, contenu, updated_at: new Date().toISOString() })
      .eq('id', existing.id);
  } else {
    await supabase
      .from('zifek_prompts')
      .insert({ prompt_key: key, titre, contenu });
  }
}

/** Supprime un prompt personnalisé (retour au prompt par défaut). */
export async function deletePrompt(key: string): Promise<void> {
  try {
    await supabase.from('zifek_prompts').delete().eq('prompt_key', key);
  } catch {
    // best-effort
  }
}

/** Liste des placeholders disponibles pour la conversion. */
export const CONVERSION_PROMPT_PLACEHOLDERS: { token: string; description: string }[] = [
  { token: '{{CATEGORIE}}', description: 'Nom de la catégorie de thème sélectionnée' },
  { token: '{{TYPE_BLOCK}}', description: 'Bloc « type de thème » (déjà préfixé de 2 sauts de ligne)' },
  { token: '{{BUSINESS_BLOCK}}', description: "Bloc « catégorie d'activité » (produits / services / mixte)" },
  { token: '{{REFERENCE_BLOCK}}', description: 'Instructions du fichier de référence (ou message par défaut)' },
  { token: '{{PAGES_LIST}}', description: 'Liste des pages « - clé : libellé » sur plusieurs lignes' },
  { token: '{{PAGES_KEYS}}', description: 'Liste des clés de pages séparées par des virgules' },
];